import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  TrendingUp,
  MessageSquare,
  Circle,
  Loader2,
  MoreVertical,
  Pause,
  Play,
  RefreshCw,
  Settings,
  Users,
  FileText,
  Link2,
  X,
  BarChart3,
  Zap,
  Server,
  Brain,
  Activity,
  GitBranch,
  Network,
  CheckCircle,
  AlertTriangle,
  Trash2,
  AlertCircle,
} from "lucide-react";
import RevSyncAgentSettings from "../agents/RevSyncAgentSettings";
import OnboardingChat from "../agents/OnboardingChat";
import ReconciliationModal from "../agents/ReconciliationModal";
import RevOpsChat from "../common/RevOpsChat";
import ChatActionPanel from "../common/ChatActionPanel";
import { useAuth } from "../../context/AuthContext";
import { AppConfig } from "../../utils/Constants";
import { isStaging, isDevelopment } from "../../utils/Environment";
import { hubSpotService } from "../../services/HubSpotService";
import { stripeIntegrationService } from "../../services/StripeIntegrationService";
import {
  revSyncService,
  AgentStatus,
  Prerequisites,
} from "../../services/RevSyncService";
import { useToastContext } from "../../context/ToastContext";
import { useSettingsContext } from "../../context/SettingsContext";
import HoneycombCluster from "../common/HoneycombCluster";
import EnvironmentBadge from "../common/EnvironmentBadge";
import hubspotLogo from "../../assets/hubspot/hubspot_orange.png";
import stripeLogoPurple from "../../assets/stripe/stripe_purple.png";

interface Agent {
  id: string;
  name: string;
  agentName?: string; // Fun/catchy agent name displayed below main name
  description: string;
  icon: React.ReactNode;
  features: string[];
  status: "available" | "coming-soon";
  route?: string;
  category?: string;
  price?: number; // Monthly price in USD
  priceId?: string; // Stripe price ID
}

const REVOPS_WORDS = [
  { name: "Marketplace", color: "#F57E0C" }, // Orange - brand color
  { name: "Agents", color: "#4A90E2" }, // Blue
  { name: "Tools", color: "#656565" }, // Gray
  { name: "Platform", color: "#00A1E0" }, // Cyan
  { name: "Solutions", color: "#1363FF" }, // Bright Blue
  { name: "Automation", color: "#9B59B6" }, // Purple
  { name: "Intelligence", color: "#FFCD00" }, // Yellow
  { name: "Workflows", color: "#3498DB" }, // Sky Blue
  { name: "Integrations", color: "#FF3366" }, // Pink
  { name: "Analytics", color: "#E67E22" }, // Orange
  { name: "Insights", color: "#635BFF" }, // Indigo
  { name: "Operations", color: "#2ECC71" }, // Green
  { name: "Services", color: "#00A3DE" }, // Teal
  { name: "Hub", color: "#F1C40F" }, // Gold
  { name: "Network", color: "#0066CC" }, // Navy
  { name: "Ecosystem", color: "#1ABC9C" }, // Turquoise
  { name: "Suite", color: "#E74C3C" }, // Red
  { name: "Stack", color: "#FF4A00" }, // Orange Red
  { name: "System", color: "#00BF6F" }, // Green
  { name: "Framework", color: "#6C47FF" }, // Purple
];

const Home: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { healthStatus } = useAuth();
  const { showToast } = useToastContext();
  const { setIsSettingsOpen, setActiveSettingsTab } = useSettingsContext();
  const [connectedCount, setConnectedCount] = useState(0);
  const [isLoadingConnections, setIsLoadingConnections] = useState(true);
  const [loadingAgentId, setLoadingAgentId] = useState<string | null>(null);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  const [isEntering, setIsEntering] = useState(false);
  const [isRevSyncSettingsOpen, setIsRevSyncSettingsOpen] = useState(false);
  const [isSettingsClosing, setIsSettingsClosing] = useState(false);
  const [isSettingsOpening, setIsSettingsOpening] = useState(false);
  const [isReconciliationOpen, setIsReconciliationOpen] = useState(false);
  const [isReconciliationClosing, setIsReconciliationClosing] = useState(false);
  const [isReconciliationOpening, setIsReconciliationOpening] = useState(false);
  const [isMenuModalOpen, setIsMenuModalOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const deployButtonRef = useRef<HTMLButtonElement>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isChatClosing, setIsChatClosing] = useState(false);
  const [isChatOpening, setIsChatOpening] = useState(false);
  const [activeActionView, setActiveActionView] = useState<string | null>(null);
  const [isOnboardingChatOpen, setIsOnboardingChatOpen] = useState(false);
  const [isOnboardingChatClosing, setIsOnboardingChatClosing] = useState(false);
  const [isOnboardingChatOpening, setIsOnboardingChatOpening] = useState(false);
  const [isClearDataModalOpen, setIsClearDataModalOpen] = useState(false);
  const [isClearingData, setIsClearingData] = useState(false);
  const [isPauseConfirmModalOpen, setIsPauseConfirmModalOpen] = useState(false);

  // Update modal position on window resize
  useEffect(() => {
    const updatePosition = () => {
      if (isMenuModalOpen && menuButtonRef.current) {
        const buttonRect = menuButtonRef.current.getBoundingClientRect();
        const deployRect = deployButtonRef.current?.getBoundingClientRect();

        setMenuPosition({
          top: deployRect ? deployRect.top : buttonRect.bottom + 8,
          left: buttonRect.right - 280,
        });
      }
    };

    if (isMenuModalOpen) {
      window.addEventListener("resize", updatePosition);
      updatePosition(); // Initial position update
      return () => window.removeEventListener("resize", updatePosition);
    }
  }, [isMenuModalOpen]);
  const [agentStatus, setAgentStatus] = useState<AgentStatus | null>(null);
  const [prerequisites, setPrerequisites] = useState<Prerequisites | null>(
    null
  );
  const [isLoadingAgentStatus, setIsLoadingAgentStatus] = useState(true);
  const [isEnablingAgent, setIsEnablingAgent] = useState(false);
  const [isPausingAgent, setIsPausingAgent] = useState(false);
  const [isResumingAgent, setIsResumingAgent] = useState(false);
  const [backendError, setBackendError] = useState(false);

  const loadAgentScreen = async () => {
    setIsLoadingAgentStatus(true);
    setBackendError(false);
    try {
      const [prereqs, status] = await Promise.allSettled([
        revSyncService.checkPrerequisites(),
        revSyncService.getAgentStatus().catch(() => null),
      ]);

      // Check if requests failed (network errors, 5xx, etc.)
      const prereqsFailed = prereqs.status === "rejected";
      const statusFailed = status.status === "rejected";

      // Determine if this is a backend connectivity issue
      // Network errors (no response) or 5xx errors indicate backend is down
      const isNetworkError = (reason: any) => {
        if (!reason) return false;
        // Network error (no response)
        if (!reason.response) return true;
        // Server error (5xx)
        if (reason.response?.status >= 500) return true;
        return false;
      };

      const prereqsIsNetworkError =
        prereqsFailed && isNetworkError(prereqs.reason);
      const statusIsNetworkError =
        statusFailed && isNetworkError(status.reason);

      // If both fail with network/server errors, backend is likely down
      if (prereqsIsNetworkError && statusIsNetworkError) {
        setBackendError(true);
        setPrerequisites(null);
        setAgentStatus(null);
        showToast(
          "error",
          "Unable to connect to backend. Please check your connection."
        );
        return;
      }

      // If prerequisites failed with network error, show error
      if (prereqsIsNetworkError) {
        setBackendError(true);
        showToast(
          "error",
          "Unable to connect to backend. Please check your connection."
        );
      } else if (prereqs.status === "fulfilled") {
        setPrerequisites(prereqs.value);
      }

      if (status.status === "fulfilled" && status.value) {
        setAgentStatus(status.value.data);
      }
      // Status failure alone might be okay (agent not configured yet), so don't set error
    } catch (error) {
      console.error("Failed to load agent screen:", error);
      setBackendError(true);
      setPrerequisites(null);
      setAgentStatus(null);
      showToast(
        "error",
        "Unable to connect to backend. Please check your connection."
      );
    } finally {
      setIsLoadingAgentStatus(false);
    }
  };

  const determineAgentStatus = ():
    | "not_configured"
    | "ready_to_enable"
    | "enabled"
    | "paused"
    | "loading" => {
    // If we're loading and don't have both prerequisites and agentStatus, return loading
    // This prevents showing wrong state while data is being fetched
    if (isLoadingAgentStatus && (!prerequisites || !agentStatus)) {
      return "loading";
    }
    // If we're loading but have both, use the current state (no change needed)
    if (isLoadingAgentStatus && prerequisites && agentStatus) {
      if (!agentStatus.agentEnabled) {
        return "ready_to_enable";
      }
      if (agentStatus.agentPaused) {
        return "paused";
      }
      return "enabled";
    }
    // If we don't have prerequisites yet, don't show status
    if (!prerequisites) {
      return "loading";
    }
    if (!prerequisites.canEnable) {
      return "not_configured";
    }
    // If we don't have agentStatus yet, don't show status
    if (!agentStatus) {
      return "loading";
    }
    if (!agentStatus.agentEnabled) {
      return "ready_to_enable";
    }
    if (agentStatus.agentPaused) {
      return "paused";
    }
    return "enabled";
  };

  const handleEnableAgent = async () => {
    setIsEnablingAgent(true);
    const previousStatus = agentStatus;

    // Optimistic update
    if (agentStatus) {
      setAgentStatus({
        ...agentStatus,
        agentEnabled: true,
        agentEnabledAt: new Date().toISOString(),
      });
    }

    try {
      // Backend automatically queues backfill reconciliation to SQS
      const { data, userMessage } = await revSyncService.enableAgent();

      // Update with actual response
      if (agentStatus) {
        setAgentStatus({
          ...agentStatus,
          agentEnabled: data.agentEnabled,
          agentEnabledAt: data.agentEnabledAt,
        });
      }

      // Show backend message - backend handles SQS queuing automatically
      const message =
        userMessage ||
        "Agent enabled! Initial sync is running in the background.";
      showToast("success", message);

      // Refresh agent status
      await loadAgentScreen();
    } catch (error: any) {
      // Rollback on error
      if (previousStatus) {
        setAgentStatus(previousStatus);
      }

      const errorMessage =
        error?.response?.data?.data?.userMessage ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to enable agent";
      showToast("error", errorMessage);
    } finally {
      setIsEnablingAgent(false);
    }
  };

  const handlePauseAgent = async () => {
    setIsPausingAgent(true);
    const previousStatus = agentStatus;

    // Optimistic update
    if (agentStatus) {
      setAgentStatus({
        ...agentStatus,
        agentPaused: true,
        pausedAt: new Date().toISOString(),
      });
    }

    try {
      const { data, userMessage } = await revSyncService.pauseAgent();

      // Update with actual response
      if (agentStatus) {
        setAgentStatus({
          ...agentStatus,
          agentPaused: data.agentPaused,
          pausedAt: data.pausedAt,
        });
      }

      // Show backend message
      const message = userMessage || "Agent paused successfully";
      showToast("success", message);

      // Refresh agent status
      await loadAgentScreen();
    } catch (error: any) {
      // Rollback on error
      if (previousStatus) {
        setAgentStatus(previousStatus);
      }

      const errorMessage =
        error?.response?.data?.data?.userMessage ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to pause agent";
      showToast("error", errorMessage);
    } finally {
      setIsPausingAgent(false);
    }
  };

  const handleResumeAgent = async () => {
    setIsResumingAgent(true);
    const previousStatus = agentStatus;

    // Optimistic update
    if (agentStatus) {
      setAgentStatus({
        ...agentStatus,
        agentPaused: false,
        resumedAt: new Date().toISOString(),
      });
    }

    try {
      // Backend automatically queues reconciliation to SQS
      const { data, userMessage } = await revSyncService.resumeAgent();

      // Update with actual response
      if (agentStatus) {
        setAgentStatus({
          ...agentStatus,
          agentPaused: data.agentPaused,
          resumedAt: data.resumedAt,
        });
      }

      // Show backend message - backend handles SQS queuing automatically
      const message =
        userMessage ||
        "Agent resumed! Reconciliation is running to catch up on any missed changes.";
      showToast("success", message);

      // Refresh agent status
      await loadAgentScreen();
    } catch (error: any) {
      // Rollback on error
      if (previousStatus) {
        setAgentStatus(previousStatus);
      }

      const errorMessage =
        error?.response?.data?.data?.userMessage ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to resume agent";
      showToast("error", errorMessage);
    } finally {
      setIsResumingAgent(false);
    }
  };

  // Update connected count from prerequisites (already fetched in loadAgentScreen)
  useEffect(() => {
    if (prerequisites) {
      let count = 0;
      if (prerequisites.hubspotConnected) count++;
      if (prerequisites.stripeConnected) count++;
      setConnectedCount(count);
      setIsLoadingConnections(false);
    }
  }, [prerequisites]);

  // Load agent status and prerequisites on mount
  useEffect(() => {
    loadAgentScreen();
  }, []);

  // Open onboarding chat when returning from OAuth success screens
  useEffect(() => {
    // Only open chat if we're on the home route
    if (location.pathname !== "/") return;

    const params = new URLSearchParams(window.location.search);

    // Check for OAuth params
    if (params.get("code") || params.get("state")) {
      setIsOnboardingChatClosing(false);
      setIsOnboardingChatOpening(true);
      setIsOnboardingChatOpen(true);
      setTimeout(() => setIsOnboardingChatOpening(false), 300);
    }
  }, [location.pathname, location.search]);

  // Refresh when page becomes visible (user navigates back from Integrations page)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && location.pathname === "/") {
        // Page became visible, refresh connections
        loadAgentScreen();

        // Check for OAuth params and open chat if present
        const params = new URLSearchParams(window.location.search);
        if (params.get("code") || params.get("state")) {
          setIsOnboardingChatClosing(false);
          setIsOnboardingChatOpening(true);
          setIsOnboardingChatOpen(true);
          setTimeout(() => setIsOnboardingChatOpening(false), 300);
        }
      }
    };

    const handleConnectionChange = () => {
      // Refresh when connection status changes
      loadAgentScreen();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("connectionStatusChanged", handleConnectionChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener(
        "connectionStatusChanged",
        handleConnectionChange
      );
    };
  }, [location.pathname]);

  // Helper function to get agent status based on instructions
  const getAgentStatusFromData = (data: AgentStatus | null) => {
    if (!data) {
      return { status: "error" as const, label: "Not Enabled" };
    }
    if (!data.agentEnabled) {
      return { status: "error" as const, label: "Not Enabled" };
    }
    if (data.agentPaused) {
      return { status: "paused" as const, label: "Paused" };
    }
    return { status: "enabled" as const, label: "Enabled" };
  };

  // Poll agent status every 30 seconds if enabled
  useEffect(() => {
    if (agentStatus?.agentEnabled && !agentStatus.agentPaused) {
      const interval = setInterval(() => {
        revSyncService
          .getAgentStatus()
          .then(({ data }) => setAgentStatus(data))
          .catch((error) =>
            console.error("Failed to refresh agent status:", error)
          );
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [agentStatus?.agentEnabled, agentStatus?.agentPaused]);

  // Rotate through RevOps words - minimal fade with glow
  useEffect(() => {
    const interval = setInterval(() => {
      // Fade out current word with blur
      setIsExiting(true);
      // Change word and fade in new word
      setTimeout(() => {
        setCurrentWordIndex((prev) => (prev + 1) % REVOPS_WORDS.length);
        setIsExiting(false);
        setIsEntering(true);
        // Fade in new word after a brief moment
        setTimeout(() => {
          setIsEntering(false);
        }, 50);
      }, 700); // Wait for fade out to complete (matches transition duration)
    }, 3500); // Change word every 3.5 seconds

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isRevSyncSettingsOpen && !isSettingsClosing) {
      setIsSettingsOpening(true);
      const timer = setTimeout(() => {
        setIsSettingsOpening(false);
      }, 10);
      return () => clearTimeout(timer);
    }
  }, [isRevSyncSettingsOpen, isSettingsClosing]);

  // Open RevSync settings when navigating from Hub (e.g. Automations button)
  useEffect(() => {
    const state = location.state as { openRevSyncSettings?: boolean } | null;
    if (state?.openRevSyncSettings) {
      setIsRevSyncSettingsOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const handleOpenSettings = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsSettingsClosing(false);
    setIsSettingsOpening(true);
    setIsRevSyncSettingsOpen(true);
    setTimeout(() => {
      setIsSettingsOpening(false);
    }, 10);
  };

  const handleCloseSettings = async () => {
    if (isSettingsClosing) return; // Prevent multiple clicks
    setIsSettingsClosing(true);
    setTimeout(async () => {
      setIsRevSyncSettingsOpen(false);
      setIsSettingsClosing(false);
      // Refresh agent status when settings close (agent may have been enabled/paused)
      await loadAgentScreen();
    }, 150); // Match fade out duration
  };

  const handleOpenReconciliation = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsReconciliationClosing(false);
    setIsReconciliationOpening(true);
    setIsReconciliationOpen(true);
    setTimeout(() => {
      setIsReconciliationOpening(false);
    }, 10);
  };

  const handleCloseReconciliation = async () => {
    if (isReconciliationClosing) return;
    setIsReconciliationClosing(true);
    setTimeout(() => {
      setIsReconciliationOpen(false);
      setIsReconciliationClosing(false);
    }, 150);
  };

  const handleClearData = async () => {
    setIsClearingData(true);
    try {
      const { data } = await revSyncService.clearData();
      const counts = data.deleted;
      const countMessages = [
        `${counts.contracts} contract${counts.contracts !== 1 ? "s" : ""}`,
        `${counts.globalCustomers} customer${counts.globalCustomers !== 1 ? "s" : ""}`,
        `${counts.reconciliationRuns} reconciliation run${counts.reconciliationRuns !== 1 ? "s" : ""}`,
        `${counts.fieldMappings} field mapping${counts.fieldMappings !== 1 ? "s" : ""}`,
        `${counts.hubSpotObjectMappings} object mapping${counts.hubSpotObjectMappings !== 1 ? "s" : ""}`,
        `${counts.renewalReminders} renewal reminder${counts.renewalReminders !== 1 ? "s" : ""}`,
        `${counts.hubSpotSyncLogs} sync log${counts.hubSpotSyncLogs !== 1 ? "s" : ""}`,
      ].filter((msg) => !msg.startsWith("0"));
      const successMessage = `Data cleared successfully. Deleted: ${countMessages.join(", ")}.`;
      showToast("success", successMessage);
      setIsClearDataModalOpen(false);
      await loadAgentScreen();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to clear data";
      showToast("error", errorMessage);
    } finally {
      setIsClearingData(false);
    }
  };

  // Preload next color for smoother transitions
  useEffect(() => {
    const nextIndex = (currentWordIndex + 1) % REVOPS_WORDS.length;
    const nextColor = REVOPS_WORDS[nextIndex].color;
    const preloadDiv = document.createElement("div");
    preloadDiv.style.color = nextColor;
    preloadDiv.style.position = "absolute";
    preloadDiv.style.opacity = "0";
    preloadDiv.style.pointerEvents = "none";
    document.body.appendChild(preloadDiv);

    return () => {
      if (document.body.contains(preloadDiv)) {
        document.body.removeChild(preloadDiv);
      }
    };
  }, [currentWordIndex]);

  const agents: Agent[] = [
    {
      id: "revenue-intelligence",
      name: "Contract Management Solution",
      agentName: "RevSync Agent",
      description:
        "Connect HubSpot and Stripe to create a unified contract and revenue intelligence layer. Track contracts, ARR/MRR and customer status in real-time.",
      icon: <Brain className="w-5 h-5" />,
      features: [
        "HubSpot + Stripe integration",
        "Automated contract tracking",
        "Real-time revenue intelligence",
        "ARR/MRR calculations",
      ],
      status: "available",
      route: "/contract-intelligence/setup",
      price: 850,
      priceId: "price_1OQ012J4K2Y3Z1X2W3V4U5T6", // Replace with actual Stripe price ID
    },
    // Temporarily hidden
    // {
    //   id: "hubspot-analytics",
    //   name: "CRM Data Chat Agent",
    //   agentName: "Data Agent",
    //   description:
    //     "Chat with your HubSpot CRM data. Get instant insights, analyze deals, pipeline metrics and discuss your HubSpot instance with AI-powered conversations.",
    //   icon: <MessageSquare className="w-5 h-5" />,
    //   features: [
    //     "CRM Chat Interface",
    //     "AI-powered data discussions",
    //     "Pipeline analytics",
    //     "Deal metrics & insights",
    //   ],
    //   status: "available",
    //   route: "/hubspot-agent",
    //   price: 0, // Free agent
    //   priceId: undefined,
    // },
  ];

  const handleAgentClick = async (agent: Agent, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }

    if (agent.status === "available" && agent.route) {
      setLoadingAgentId(agent.id);
      try {
        // For HubSpot Analytics Agent, check connection status first
        if (agent.id === "hubspot-analytics") {
          const { data } = await hubSpotService.getHubSpotStatus();
          if (data.connected) {
            // If connected, go directly to display page
            navigate("/hubspot/display");
          } else {
            // If not connected, go to the agent page
            navigate(agent.route);
          }
        } else if (agent.id === "revenue-intelligence") {
          setIsOnboardingChatClosing(false);
          setIsOnboardingChatOpening(true);
          setIsOnboardingChatOpen(true);
          setTimeout(() => setIsOnboardingChatOpening(false), 300);
        } else {
          navigate(agent.route);
        }
      } catch (error) {
        // On error, default to agent page
        console.error("Failed to check status:", error);
        if (agent.route) {
          navigate(agent.route);
        }
      } finally {
        setLoadingAgentId(null);
      }
    } else if (agent.status === "available" && !agent.route) {
      console.log(`${agent.name} is available but route not configured yet`);
    }
  };

  const availableAgents = agents.filter((a) => a.status === "available");

  return (
    <>
      <div className="w-full min-h-screen bg-background-default dark:bg-background-dark relative overflow-hidden">
        <div className="make-dot-pattern"></div>
        <div className="max-w-4xl mx-auto px-8 pt-36 pb-16 relative z-10">
          {/* Header Section - Super simple */}
          <div className="mb-12 pb-8 border-b border-divider dark:border-divider-dark">
            <h1
              className="text-3xl font-semibold text-black dark:text-text-primaryDark mb-2 tracking-tight"
              style={{ textShadow: "none", filter: "none" }}
            >
              RevOps{" "}
              <span className="relative inline-block min-w-[120px] align-baseline">
                <span
                  key={currentWordIndex}
                  className="relative inline-block transition-all duration-700 ease-in-out"
                  style={{
                    transitionProperty: "opacity, filter, color",
                    transitionDuration: "700ms",
                    willChange: "opacity, filter",
                    opacity: isEntering || isExiting ? 0 : 1,
                    color: REVOPS_WORDS[currentWordIndex].color,
                    filter: isEntering
                      ? "blur(6px)"
                      : isExiting
                      ? "blur(6px)"
                      : `blur(0px) drop-shadow(0 0 6px ${REVOPS_WORDS[currentWordIndex].color}30)`,
                    pointerEvents: isEntering || isExiting ? "none" : "auto",
                  }}
                >
                  {REVOPS_WORDS[currentWordIndex].name}
                </span>
              </span>
            </h1>
            <p className="text-base text-black dark:text-text-secondaryDark mb-6">
              Connect your tools and automate your revenue operations
            </p>

            {/* Quick Stats */}
            <div className="flex items-center gap-6 mt-8">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-black dark:bg-text-primaryDark rounded-full"></div>
                <span className="text-sm text-black dark:text-text-secondaryDark">
                  {availableAgents.length}{" "}
                  {availableAgents.length === 1 ? "agent" : "agents"} available
                </span>
              </div>
              {!isLoadingConnections && (
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-black dark:bg-text-primaryDark rounded-full"></div>
                  <span className="text-sm text-black dark:text-text-secondaryDark">
                    {connectedCount}{" "}
                    {connectedCount === 1 ? "integration" : "integrations"}{" "}
                    active
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Available Agents - Super simple */}
          <div className="flex-1">
            {/* Backend Error State */}
            {backendError && !isLoadingAgentStatus && (
              <div className="bg-background-paper dark:bg-background-paperDark rounded-lg p-6 border border-red-200 dark:border-red-800/50">
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg flex-shrink-0">
                    <Server className="w-5 h-5 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-semibold text-text-primary dark:text-text-primaryDark mb-1">
                      Unable to connect to backend
                    </h3>
                    <p className="text-sm text-text-secondary dark:text-text-secondaryDark mb-4">
                      We're having trouble connecting to our servers. Please
                      check your connection and try again.
                    </p>
                    <button
                      onClick={loadAgentScreen}
                      disabled={isLoadingAgentStatus}
                      className="px-4 py-2 text-sm font-medium rounded-lg bg-black dark:bg-white text-white dark:text-black hover:bg-black/90 dark:hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {isLoadingAgentStatus ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Retrying...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4" />
                          Retry
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Normal Agent List */}
            {!backendError && availableAgents.length > 0 && (
              <div>
                <div className="space-y-3">
                  {availableAgents.map((agent) => (
                    <div
                      key={agent.id}
                      className={`group relative rounded-lg p-4 border transition-all duration-200 ${
                        agent.id === "revenue-intelligence"
                          ? "bg-background-paper dark:bg-[#141414] border-divider dark:border-divider-dark hover:border-divider dark:hover:border-divider-dark hover:shadow-md"
                          : "bg-background-paper dark:bg-background-paperDark border-divider dark:border-divider-dark hover:border-divider dark:hover:border-divider-dark hover:shadow-md"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {/* Icon */}
                          {agent.id === "revenue-intelligence" ? (
                            <div
                              className={`p-2 bg-background-panel dark:bg-background-paperDark/50 border border-divider/50 dark:border-white/15 rounded-lg flex-shrink-0 relative transition-all ${
                                determineAgentStatus() === "loading"
                                  ? "opacity-60 blur-[2px]"
                                  : ""
                              }`}
                            >
                              <Brain
                                className={`w-5 h-5 transition-colors ${
                                  determineAgentStatus() === "enabled"
                                    ? "text-green-600 dark:text-green-400"
                                    : determineAgentStatus() === "paused"
                                    ? "text-amber-600 dark:text-amber-400"
                                    : determineAgentStatus() === "loading"
                                    ? "text-blue-600 dark:text-blue-400"
                                    : "text-black dark:text-text-primaryDark"
                                }`}
                              />
                            </div>
                          ) : (
                            <div className="p-2 bg-background-panel dark:bg-background-paperDark/50 border border-divider/50 dark:border-white/15 rounded-lg text-black dark:text-text-primaryDark flex-shrink-0">
                              {agent.icon}
                            </div>
                          )}

                          {/* Name and Status */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <h3 className="text-lg font-semibold text-black dark:text-text-primaryDark">
                                {agent.name}
                              </h3>
                            </div>
                            {agent.agentName && (
                              <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
                                {agent.agentName}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {agent.id === "revenue-intelligence" &&
                          prerequisites?.hubspotConnected &&
                          prerequisites?.stripeConnected &&
                          (determineAgentStatus() === "enabled" ||
                            determineAgentStatus() === "paused") ? (
                            <div className="inline-flex items-stretch rounded-lg border border-divider dark:border-divider-dark bg-background-panel/80 dark:bg-background-panelDark/80 shadow-sm overflow-hidden">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  navigate("/revsync");
                                }}
                                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-none border-0 border-r border-divider dark:border-divider-dark bg-transparent text-text-primary dark:text-text-primaryDark hover:bg-black/5 dark:hover:bg-white/5 transition-colors relative z-20 pointer-events-auto"
                              >
                                Open Hub
                              </button>
                              <button
                                ref={deployButtonRef}
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (determineAgentStatus() === "enabled") {
                                    setIsPauseConfirmModalOpen(true);
                                  } else if (
                                    determineAgentStatus() === "paused"
                                  ) {
                                    handleResumeAgent();
                                  }
                                }}
                                disabled={isPausingAgent || isResumingAgent}
                                title={
                                  determineAgentStatus() === "enabled"
                                    ? "Pause agent"
                                    : "Resume agent"
                                }
                                className={`
                                  group inline-flex items-center justify-center gap-2 px-3 py-2 border-0 rounded-none transition-all duration-200 relative z-20
                                  ${
                                    determineAgentStatus() === "enabled"
                                      ? "bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/40 text-green-700 dark:text-green-400"
                                      : "bg-amber-50 dark:bg-yellow-900/30 hover:bg-amber-100 dark:hover:bg-yellow-900/40 text-amber-700 dark:text-yellow-400"
                                  }
                                  ${
                                    isPausingAgent || isResumingAgent
                                      ? "opacity-50 cursor-not-allowed"
                                      : "cursor-pointer"
                                  }
                                `}
                              >
                                {isPausingAgent || isResumingAgent ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                                ) : (
                                  <>
                                    <div
                                      className={`
                                        w-1.5 h-1.5 rounded-full shrink-0 bg-current
                                        ${
                                          determineAgentStatus() === "enabled" &&
                                          !isPausingAgent
                                            ? "animate-pulse"
                                            : ""
                                        }
                                      `}
                                    />
                                    {determineAgentStatus() === "enabled" ? (
                                      <Pause
                                        className="w-3.5 h-3.5 shrink-0 group-hover:opacity-90 transition-opacity"
                                        strokeWidth={2}
                                      />
                                    ) : (
                                      <Play
                                        className="w-3.5 h-3.5 shrink-0 ml-0.5 group-hover:opacity-90 transition-opacity"
                                        strokeWidth={2}
                                        fill="currentColor"
                                      />
                                    )}
                                  </>
                                )}
                              </button>
                            </div>
                          ) : agent.id === "revenue-intelligence" ? (
                            <>
                              {determineAgentStatus() === "ready_to_enable" && (
                                <button
                                  type="button"
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();

                                    setIsOnboardingChatClosing(false);
                                    setIsOnboardingChatOpening(true);
                                    setIsOnboardingChatOpen(true);
                                    setTimeout(
                                      () => setIsOnboardingChatOpening(false),
                                      300
                                    );
                                  }}
                                  disabled={isEnablingAgent}
                                  className="px-5 py-2.5 text-sm font-medium rounded-md border border-divider dark:border-divider-dark bg-background-paper dark:bg-[#141414] text-text-primary dark:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark hover:border-divider dark:hover:border-divider-dark active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed relative z-20 pointer-events-auto"
                                >
                                  {isEnablingAgent ? (
                                    <span className="flex items-center gap-2">
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                      Enabling...
                                    </span>
                                  ) : (
                                    "Deploy"
                                  )}
                                </button>
                              )}
                              {determineAgentStatus() === "not_configured" &&
                                !isLoadingAgentStatus && (
                                  <button
                                    ref={deployButtonRef}
                                    type="button"
                                    onClick={async (e) => {
                                      e.preventDefault();
                                      e.stopPropagation();

                                      if (agent.id === "revenue-intelligence") {
                                        setIsOnboardingChatClosing(false);
                                        setIsOnboardingChatOpening(true);
                                        setIsOnboardingChatOpen(true);
                                        setTimeout(
                                          () =>
                                            setIsOnboardingChatOpening(false),
                                          300
                                        );
                                      } else {
                                        handleAgentClick(agent, e);
                                      }
                                    }}
                                    disabled={
                                      loadingAgentId === agent.id ||
                                      isEnablingAgent
                                    }
                                    className="px-5 py-2.5 text-sm font-medium rounded-md border border-divider dark:border-divider-dark bg-background-paper dark:bg-[#141414] text-text-primary dark:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark hover:border-divider dark:hover:border-divider-dark active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed relative z-20 pointer-events-auto"
                                  >
                                    {loadingAgentId === agent.id ||
                                    isEnablingAgent ? (
                                      <span className="flex items-center gap-2">
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                        {isEnablingAgent
                                          ? "Enabling..."
                                          : "Loading..."}
                                      </span>
                                    ) : (
                                      "Deploy"
                                    )}
                                  </button>
                                )}
                              {/* Show loading state while agent status is being fetched */}
                              {determineAgentStatus() === "loading" && (
                                <div className="px-5 py-2.5 text-sm font-medium rounded-md relative z-20 pointer-events-auto shadow-sm h-fit bg-background-panel dark:bg-background-panelDark border border-divider dark:border-divider-dark flex items-center justify-center">
                                  <Loader2 className="w-4 h-4 animate-spin text-text-secondary dark:text-text-secondaryDark" />
                                </div>
                              )}
                            </>
                          ) : agent.route ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleAgentClick(agent, e);
                              }}
                              disabled={loadingAgentId === agent.id}
                              className="px-5 py-2.5 text-sm font-medium rounded-md active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed relative z-20 pointer-events-auto shadow-sm h-fit bg-black dark:bg-white text-white dark:text-black hover:bg-black/90 dark:hover:bg-white/90"
                            >
                              {loadingAgentId === agent.id ? (
                                <span className="flex items-center gap-2">
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                  Loading...
                                </span>
                              ) : (
                                "Deploy"
                              )}
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* No agents available (only show if no backend error) */}
            {!backendError &&
              availableAgents.length === 0 &&
              !isLoadingAgentStatus && (
                <div className="text-center py-12">
                  <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
                    No agents available
                  </p>
                </div>
              )}
          </div>
        </div>
      </div>
      <HoneycombCluster />

      {/* RevSync Agent Settings */}
      <RevSyncAgentSettings
        isOpen={isRevSyncSettingsOpen}
        onClose={handleCloseSettings}
        isClosing={isSettingsClosing}
        isOpening={isSettingsOpening}
      />

      {/* Reconciliation Modal */}
      <ReconciliationModal
        isOpen={isReconciliationOpen}
        onClose={handleCloseReconciliation}
        isClosing={isReconciliationClosing}
        isOpening={isReconciliationOpening}
      />

      {/* RevOps Chat */}
      <RevOpsChat
        isOpen={isChatOpen}
        onClose={() => {
          setIsChatClosing(true);
          setActiveActionView(null);
          setTimeout(() => {
            setIsChatOpen(false);
            setIsChatClosing(false);
          }, 200);
        }}
        isClosing={isChatClosing}
        isOpening={isChatOpening}
        onActionViewChange={(viewType) => {
          setActiveActionView(viewType);
        }}
      />

      <OnboardingChat
        isOpen={isOnboardingChatOpen}
        isClosing={isOnboardingChatClosing}
        isOpening={isOnboardingChatOpening}
        onClose={() => {
          if (isOnboardingChatClosing) return;
          setIsOnboardingChatClosing(true);
          setTimeout(() => {
            setIsOnboardingChatOpen(false);
            setIsOnboardingChatClosing(false);
            loadAgentScreen();
          }, 200);
        }}
      />

      {/* Chat Action Panel - Right Side */}
      <ChatActionPanel
        viewType={activeActionView}
        onClose={() => {
          setActiveActionView(null);
        }}
      />

      {/* Chat Toggle Button - Hidden for now */}
      {false && (
        <button
          onClick={() => {
            if (!isChatOpen) {
              setIsChatOpening(true);
              setIsChatOpen(true);
              setTimeout(() => setIsChatOpening(false), 300);
            } else {
              setIsChatClosing(true);
              setTimeout(() => {
                setIsChatOpen(false);
                setIsChatClosing(false);
              }, 200);
            }
          }}
          className="fixed bottom-6 right-6 w-16 h-16 bg-secondary hover:bg-secondary-hover text-white rounded-full shadow-2xl flex items-center justify-center transition-all hover:scale-110 z-[9999]"
          title={
            isChatOpen ? "Close RevOps Agent Chat" : "Open RevOps Agent Chat"
          }
          style={{
            boxShadow: "0 10px 25px rgba(245, 126, 12, 0.4)",
            position: "fixed",
          }}
        >
          {isChatOpen ? (
            <X className="w-7 h-7" />
          ) : (
            <MessageSquare className="w-7 h-7" />
          )}
        </button>
      )}

      {/* Agent Menu - Magical & Agentic */}
      {isMenuModalOpen && (
        <>
          {/* Subtle backdrop */}
          <div
            className="fixed inset-0 z-[99] bg-black/5 dark:bg-black/20 backdrop-blur-[2px]"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsMenuModalOpen(false);
            }}
          />

          {/* Floating Orb Menu - Organic & Subtle */}
          <div
            className="fixed z-[100] overflow-visible opacity-0 animate-fadeIn"
            style={{
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Main container - Pill/Orb shape */}
            <div
              className="relative bg-white/95 dark:bg-[#141414]/95 backdrop-blur-xl rounded-[28px] shadow-xl w-60 border border-divider/20 dark:border-white/10 overflow-hidden"
              style={{
                clipPath: "ellipse(100% 92% at 50% 50%)",
              }}
            >
              {/* Content */}
              <div className="relative py-3 px-2">
                {/* Connections - Compact Status */}
                <div className="px-3 py-2 mb-1">
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsMenuModalOpen(false);
                        if (
                          prerequisites?.hubspotConnected &&
                          prerequisites?.stripeConnected
                        ) {
                          setActiveSettingsTab("integrations");
                          setIsSettingsOpen(true);
                        } else {
                          navigate("/contract-intelligence/setup");
                        }
                      }}
                      className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-full transition-all hover:bg-[#FF7A59]/10 dark:hover:bg-[#FF7A59]/5 group"
                      title="HubSpot"
                    >
                      <div
                        className={`w-1.5 h-1.5 rounded-full transition-all ${
                          prerequisites?.hubspotConnected
                            ? "bg-[#FF7A59]"
                            : "bg-[#FF7A59]/30"
                        }`}
                      />
                      <span className="text-xs font-medium text-text-primary dark:text-text-primaryDark opacity-70 group-hover:opacity-100">
                        HubSpot
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsMenuModalOpen(false);
                        if (
                          prerequisites?.hubspotConnected &&
                          prerequisites?.stripeConnected
                        ) {
                          setActiveSettingsTab("integrations");
                          setIsSettingsOpen(true);
                        } else {
                          navigate("/contract-intelligence/setup");
                        }
                      }}
                      className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-full transition-all hover:bg-[#635BFF]/10 dark:hover:bg-[#635BFF]/5 group"
                      title="Stripe"
                    >
                      <div
                        className={`w-1.5 h-1.5 rounded-full transition-all ${
                          prerequisites?.stripeConnected
                            ? "bg-[#635BFF]"
                            : "bg-[#635BFF]/30"
                        }`}
                      />
                      <span className="text-xs font-medium text-text-primary dark:text-text-primaryDark opacity-70 group-hover:opacity-100">
                        Stripe
                      </span>
                    </button>
                  </div>
                </div>

                {/* Divider - Ultra subtle */}
                <div className="h-px bg-divider/10 dark:bg-white/5 my-1.5 mx-3" />

                {/* Actions - Clean layout */}
                <div className="space-y-0.5 px-1.5">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsMenuModalOpen(false);
                      navigate("/customers");
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-text-primary dark:text-text-primaryDark hover:bg-background-panel/50 dark:hover:bg-background-panelDark/50 rounded-lg transition-all group"
                  >
                    <Users className="w-3.5 h-3.5 text-text-secondary dark:text-text-secondaryDark group-hover:text-text-primary dark:group-hover:text-text-primaryDark transition-colors flex-shrink-0" />
                    <span className="text-xs font-medium">Customers</span>
                  </button>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsMenuModalOpen(false);
                      navigate("/contracts");
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-text-primary dark:text-text-primaryDark hover:bg-background-panel/50 dark:hover:bg-background-panelDark/50 rounded-lg transition-all group"
                  >
                    <FileText className="w-3.5 h-3.5 text-text-secondary dark:text-text-secondaryDark group-hover:text-text-primary dark:group-hover:text-text-primaryDark transition-colors flex-shrink-0" />
                    <span className="text-xs font-medium">Contracts</span>
                  </button>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsMenuModalOpen(false);
                      handleOpenReconciliation(e);
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-text-primary dark:text-text-primaryDark hover:bg-background-panel/50 dark:hover:bg-background-panelDark/50 rounded-lg transition-all group"
                  >
                    <BarChart3 className="w-3.5 h-3.5 text-text-secondary dark:text-text-secondaryDark group-hover:text-text-primary dark:group-hover:text-text-primaryDark transition-colors flex-shrink-0" />
                    <span className="text-xs font-medium">Reconcile</span>
                  </button>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsMenuModalOpen(false);
                      handleOpenSettings(e);
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-text-primary dark:text-text-primaryDark hover:bg-background-panel/50 dark:hover:bg-background-panelDark/50 rounded-lg transition-all group"
                  >
                    <Zap className="w-3.5 h-3.5 text-text-secondary dark:text-text-secondaryDark group-hover:text-text-primary dark:group-hover:text-text-primaryDark transition-colors flex-shrink-0" />
                    <span className="text-xs font-medium">Automations</span>
                  </button>
                </div>

                {/* Divider - Ultra subtle */}
                <div className="h-px bg-divider/10 dark:bg-white/5 my-1.5 mx-3" />

                {/* RevSync Actions - Highlighted section */}
                <div className="space-y-0.5 px-1.5">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsMenuModalOpen(false);
                      navigate("/revsync");
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-all group bg-secondary/5 dark:bg-secondary/10 hover:bg-secondary/10 dark:hover:bg-secondary/15 border border-secondary/20 dark:border-secondary/30"
                  >
                    <Link2 className="w-3.5 h-3.5 text-secondary group-hover:text-secondary-hover transition-colors flex-shrink-0" />
                    <span className="text-xs font-medium text-secondary">
                      RevSync Hub
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsMenuModalOpen(false);
                      navigate("/revsync/approvals");
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-text-primary dark:text-text-primaryDark hover:bg-background-panel/50 dark:hover:bg-background-panelDark/50 rounded-lg transition-all group"
                  >
                    <span className="text-xs font-medium">
                      Contract Management
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Fixed Footer */}
      <footer className="fixed bottom-0 left-0 right-0 h-[48px] bg-black dark:bg-background-dark border-t border-black dark:border-divider-dark/30 flex items-center justify-center z-10">
        <div className="flex items-center gap-6 text-xs text-white dark:text-text-secondaryDark">
          {/* System Status */}
          <div className="flex items-center gap-2">
            {/* Always green for now, or use healthStatus if available */}
            {(() => {
              const isSystemHealthy =
                healthStatus?.dbState === "connected" || true; // Always green for now
              return isSystemHealthy ? (
                <div className="relative">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <div className="absolute inset-0 w-2 h-2 bg-green-500 rounded-full animate-ping opacity-75"></div>
                  <div
                    className="absolute inset-0 w-2 h-2 bg-green-500/30 rounded-full animate-ping"
                    style={{ animationDelay: "0.5s" }}
                  ></div>
                </div>
              ) : (
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              );
            })()}
            <span className="text-xs text-white dark:text-text-secondaryDark">
              System Status
            </span>
          </div>
          <span className="text-white dark:text-text-secondaryDark">
            v{AppConfig.VERSION}
          </span>
          <EnvironmentBadge />
          {(isStaging || isDevelopment) && (
            <button
              type="button"
              onClick={() => setIsClearDataModalOpen(true)}
              className="p-2 rounded-lg text-white/80 dark:text-text-secondaryDark hover:text-red-400 dark:hover:text-red-400 hover:bg-red-500/20 dark:hover:bg-red-500/20 transition-colors"
              aria-label="Clear RevSync data"
              title="Clear RevSync data (testing)"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </footer>

      {/* Pause RevSync Confirmation Modal */}
      {isPauseConfirmModalOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-md z-50"
            onClick={() =>
              !isPausingAgent && setIsPauseConfirmModalOpen(false)
            }
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="bg-background-paper dark:bg-background-paperDark rounded-lg shadow-2xl max-w-md w-full border border-divider dark:border-divider-dark"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0 p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <Brain className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-text-primary dark:text-text-primaryDark mb-2">
                      Pause RevSync?
                    </h3>
                    <p className="text-sm text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                      RevSync will pause all background work. You can resume
                      anytime from RevSync Hub.
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setIsPauseConfirmModalOpen(false)}
                    disabled={isPausingAgent}
                    className="px-4 py-2 text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-text-primary dark:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      await handlePauseAgent();
                      setIsPauseConfirmModalOpen(false);
                    }}
                    disabled={isPausingAgent}
                    className="px-4 py-2 text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-green-600 hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700 text-white flex items-center gap-2"
                  >
                    {isPausingAgent ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Pausing...
                      </>
                    ) : (
                      "Pause"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Clear Data Confirmation Modal — staging/dev only */}
      {isClearDataModalOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-md z-50"
            onClick={() => !isClearingData && setIsClearDataModalOpen(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="bg-background-paper dark:bg-background-paperDark rounded-lg shadow-2xl max-w-md w-full border border-divider dark:border-divider-dark"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0 p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                    <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-text-primary dark:text-text-primaryDark mb-2">
                      Clear All RevSync Data
                    </h3>
                    <p className="text-sm text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                      This will permanently delete all RevSync data including
                      contracts, customers, reconciliation runs, field mappings,
                      and sync logs. The agent will be uninstalled and settings
                      will be reset. This action cannot be undone.
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setIsClearDataModalOpen(false)}
                    disabled={isClearingData}
                    className="px-4 py-2 text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-text-primary dark:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleClearData}
                    disabled={isClearingData}
                    className="px-4 py-2 text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-red-500 hover:bg-red-600 text-white flex items-center gap-2"
                  >
                    {isClearingData ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Clearing...
                      </>
                    ) : (
                      "Clear Data"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Home;
